/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import { useNavigate } from 'react-router-dom'
import Stepper from 'views/components/Stepper'
import { MainLayout } from 'views/layouts'
import { Badge, CardWithBadgeContainer, Container, HeaderContainer, Row, SparkleIcon } from './styled'
import { H2, P } from 'views/components/UI/Text'
import { Button, BackButton } from 'views/components/UI/Buttons'
import CardTypeTest from 'views/components/Cards/SelectTest'
import brainImage from 'assets/images/brain-icon.png'
import boxImage from 'assets/images/box-icon.png'
import threeSixtyImage from 'assets/images/360-icon.png'
import { platformAdminRoutes } from 'router/routes'
import { STEPS_NEW_PROCESS, STEPS_NEW_PROCESS_360 } from '../constants'
import {
  stepThreeSelector,
  setStepThreeTrigger,
  stepTwoSelector,
  stepOneSelector,
  stepFiveSelector,
  setUserListAdded,
  setStepTwoTrigger,
} from 'ducks/createProcess'
import { useTranslation } from 'react-i18next'
import { useGetCreditsAvailables } from 'hooks/inventory/useInventory'
import { User } from '../Users/constants'
import useDeleteUsersInProcess from 'hooks/process/useDeleteUsersInProcess/use-delete-users-in-process'
import { deleteModelProcessAssociation } from 'views/screens/super-admin/homologation-skills/services/deleteModelProcessAssociation'
import { selectCompanyData, setCompanyData } from 'ducks/company'
import sparkle from '../../../../../components/assets/sparkle.png'
import getOneCompany from 'services/companies/getOneCompany'
import { useNotification } from 'lib/context/notification.context'
import {
  set360Data,
  setEvaluatedUsers,
  setEvaluators,
  setHas360Test,
  setShowIntro,
  setShowSummary,
} from 'ducks/test360'

type TypeTests = 'betesa' | 'skills' | '360'

const SelectTestsType = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getError } = useNotification()
  const stepOneData = useSelector(stepOneSelector)
  const stepTwoData = useSelector(stepTwoSelector)
  const stepThreeData = useSelector(stepThreeSelector)
  const stepFiveData = useSelector(stepFiveSelector)
  const companyInfo = useSelector(selectCompanyData)
  const InventoryTypeIlimit = 3
  const InventoryTypeIlimitIntegration = 4
  const OPERATIVE_LEVEL = 4
  const TACTICAL_LEVEL = 3
  const STRATEGIC_LEVEL = 2
  const [testsSelected, setTestsSelected] = useState<TypeTests[]>([])

  const { refreshCredits } = useGetCreditsAvailables(stepOneData.inventoryTypeId)

  const { deleteUsersInProcess, deleteTestsInProcess } = useDeleteUsersInProcess()

  useEffect(() => {
    getOneCompany(stepOneData.companyId)
      .then((response) => {
        if (response.status === 'OK') {
          dispatch(setCompanyData(response.data))
        }
      })
      .catch(() => {
        getError('Ha ocurrido un error al intentar obtener la información de la compañía')
      })
    if (stepFiveData?.userListAdded) {
      dispatch(setUserListAdded([]))

      const idUsersToDelete = stepFiveData?.userListAdded
        .map((user: User) => user.id)
        .filter((id: number) => id !== undefined)

      if (idUsersToDelete.length > 0) {
        const deleteUsers = async () => {
          await deleteUsersInProcess(stepOneData.processId, idUsersToDelete)
        }
        deleteUsers()
      }
    }
    dispatch(set360Data([]))
    dispatch(setShowSummary(false))
    dispatch(setShowIntro(true))
    dispatch(setEvaluatedUsers([]))
    dispatch(setEvaluators([]))
    dispatch(setHas360Test(false))

    const deleteModelProcess = async () => {
      await deleteModelProcessAssociation(stepOneData.processId)
    }
    const deleteTests = async () => {
      await deleteTestsInProcess(stepOneData.processId)
    }
    setTimeout(() => {
      deleteTests()
      deleteModelProcess()
    }, 500)

    refreshCredits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (stepOneData?.inventoryType === 5) {
      dispatch(setStepTwoTrigger.run({ isRoleFamilySelected: false, processId: stepOneData.processId }))
    }
  }, [dispatch, stepOneData?.inventoryType, stepOneData.processId])

  const handleSelectTest = (test: TypeTests) => {
    if (_.includes(testsSelected, test)) {
      setTestsSelected(_.without(testsSelected, test))
    } else {
      setTestsSelected((prevTestsSelected) => [...prevTestsSelected, test])
    }
  }

  const handleNextStep = () => {
    const dataStepThreeToSave = {
      processId: stepTwoData?.processId,
      onlineCourse: false,
      useBetesaTest: testsSelected.includes('betesa'),
      useSkillsTest: testsSelected.includes('skills'),
      use360Test: testsSelected.includes('360'),
    }

    try {
      dispatch(setStepThreeTrigger.run(dataStepThreeToSave))
      if (testsSelected.includes('360')) {
        dispatch(setHas360Test(true))
      }
    } catch (error) {
      return error
    } finally {
      if (testsSelected.includes('betesa')) {
        navigate(platformAdminRoutes.IdealProfileBetesa)
        return
      }
      if (testsSelected.includes('skills') && !testsSelected.includes('betesa')) {
        navigate(platformAdminRoutes.ProcessSkills)
        return
      }
      if (testsSelected.includes('360')) {
        navigate(platformAdminRoutes.Process360)
        return
      }
    }
  }

  useEffect(() => {
    if (Object.keys(stepThreeData).length > 0) {
      const listTestsSelected: TypeTests[] = []
      if (stepThreeData?.useBetesaTest) {
        listTestsSelected.push('betesa')
      }
      if (stepThreeData?.useSkillsTest) {
        listTestsSelected.push('skills')
      }
      if (stepThreeData?.use360Test) {
        listTestsSelected.push('360')
      }
      setTestsSelected(listTestsSelected)
    }
  }, [stepThreeData])

  const hasValidInventoryType = (inventoryType: number) => {
    const validTypes = [InventoryTypeIlimit, InventoryTypeIlimitIntegration]
    return validTypes.includes(inventoryType)
  }

  const isOperativeLevel = (levelName: string, level: number, inventoryType: number) => {
    return (levelName === 'Operativo' || level === OPERATIVE_LEVEL) && hasValidInventoryType(inventoryType)
  }

  const isTacticalLevel = (levelName: string, level: number) => {
    return levelName === 'Táctico' || level === TACTICAL_LEVEL
  }

  const isStrategicLevel = (levelName: string, level: number) => {
    return levelName === 'Estratégico' || level === STRATEGIC_LEVEL
  }

  const shouldShowBetesaTest = () => {
    return (
      isOperativeLevel(stepOneData.levelName, stepOneData.level, stepOneData.inventoryType) ||
      isTacticalLevel(stepOneData.levelName, stepOneData.level) ||
      isStrategicLevel(stepOneData.levelName, stepOneData.level)
    )
  }

  const handleBackButton = () => {
    if (stepOneData.inventoryType === 5) {
      navigate(platformAdminRoutes.CreateProcess)
    } else {
      navigate(platformAdminRoutes.PreconfigProcess)
    }
  }

  return (
    <MainLayout>
      <Container>
        <HeaderContainer>
          <H2 margin="0 0 30px 20px" marginBottom="30px">
            Configuración del proceso
          </H2>
          <Stepper activeStep={2} steps={stepOneData.inventoryType !== 5 ? STEPS_NEW_PROCESS : STEPS_NEW_PROCESS_360} />
        </HeaderContainer>
        <P margin="30px 0px 20px 6px">Seleccione pruebas para aplicar a los participantes</P>
        <Row>
          {shouldShowBetesaTest() && stepOneData.inventoryType !== 5 ? (
            <CardTypeTest
              title="Betesa"
              subtitle="Talentos Naturales"
              image={brainImage}
              onClick={() => handleSelectTest('betesa')}
              checked={testsSelected && (testsSelected.includes('betesa') || false)}
            />
          ) : null}
          {stepOneData.inventoryType !== 5 ? (
            <CardWithBadgeContainer>
              <CardTypeTest
                title={t('global.common.skillText')}
                subtitle="Competencias del carácter"
                image={boxImage}
                onClick={() => handleSelectTest('skills')}
                checked={testsSelected && (testsSelected.includes('skills') || false)}
              />
              {companyInfo?.usesAi && (
                <Badge>
                  Generación con IA <SparkleIcon src={sparkle} />
                </Badge>
              )}
            </CardWithBadgeContainer>
          ) : null}
          {stepOneData?.inventoryType === 5 && stepOneData?.processType === 2 ? (
              <CardTypeTest
                title={t('global.common.threeSixtyText')}
                subtitle="Prueba parametrizable"
                image={threeSixtyImage}
                onClick={() => handleSelectTest('360')}
                checked={testsSelected && (testsSelected.includes('360') || false)}
              />
          ) : null}
        </Row>
        <Row style={{ flex: 1 }} justifyContent="space-between" alignItems="flex-end">
          <BackButton onClick={handleBackButton} />
          <Button disabled={testsSelected.length < 1} type="submit" onClick={handleNextStep}>
            {t('global.common.nextButtonText')}
          </Button>
        </Row>
      </Container>
    </MainLayout>
  )
}

export default SelectTestsType
